/**
 * Device Wizard Admin Page
 * 
 * Guided wizard for admins to onboard new devices to the household.
 * Apple-clean design with Glassmorphism and Hayat branding.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFamilyAuth } from '../../../contexts/FamilyAuthContext';
import { pairDevice, startTestCall } from '../../../api/deviceApi';
import axiosClient from '../../../api/axiosClient';

// Device type configurations
const DEVICE_TYPES = [
  {
    id: 'tablet',
    label: 'Hayat Care Tablet',
    icon: '📱',
    description: 'Dediziertes Tablet für Senioren mit vorinstallierter Hayat-App'
  },
  {
    id: 'smartphone',
    label: 'Smartphone / Family App',
    icon: '📲',
    description: 'Persönliches Smartphone mit Hayat Family App'
  },
  {
    id: 'other',
    label: 'Andere kompatible Geräte',
    icon: '🖥️',
    description: 'Amazon Echo, Google Home oder andere kompatible Smart Devices'
  }
];

// Location options
const LOCATIONS = [
  { id: 'living_room', label: 'Wohnzimmer' },
  { id: 'kitchen', label: 'Küche' },
  { id: 'bedroom', label: 'Schlafzimmer' },
  { id: 'bathroom', label: 'Badezimmer' },
  { id: 'other', label: 'Sonstiges' }
];

function DeviceWizardAdminPage() {
  const { household, user, isAdmin } = useFamilyAuth();
  const navigate = useNavigate();

  // Wizard state
  const [step, setStep] = useState(1);
  const [selectedDeviceType, setSelectedDeviceType] = useState(null);
  const [pairingCode, setPairingCode] = useState('');
  const [deviceName, setDeviceName] = useState('');
  const [deviceLocation, setDeviceLocation] = useState('');
  const [selectedSeniorId, setSelectedSeniorId] = useState(null);
  
  // Data state
  const [seniors, setSeniors] = useState([]);
  const [pairedDevice, setPairedDevice] = useState(null);
  
  // UI state
  const [isPairing, setIsPairing] = useState(false);
  const [pairingError, setPairingError] = useState(null);
  const [loadingSeniors, setLoadingSeniors] = useState(false);
  const [startingCall, setStartingCall] = useState(false);

  // Redirect non-admins
  useEffect(() => {
    if (!isAdmin) {
      navigate('/family/dashboard');
    }
  }, [isAdmin, navigate]);

  // Load seniors for step 3
  useEffect(() => {
    if (step === 3 && household?.id) {
      const fetchSeniors = async () => {
        setLoadingSeniors(true);
        try {
          const response = await axiosClient.get(`/api/households/${household.id}/seniors`);
          setSeniors(response.data || []);
        } catch (error) {
          console.error('Failed to load seniors:', error);
        } finally {
          setLoadingSeniors(false);
        }
      };
      fetchSeniors();
    }
  }, [step, household?.id]);

  const handleSelectDeviceType = (type) => {
    setSelectedDeviceType(type);
    setStep(2);
  };

  const handlePairDevice = async () => {
    if (!pairingCode.trim() || !deviceName.trim()) {
      setPairingError('Bitte fülle alle Pflichtfelder aus.');
      return;
    }

    setIsPairing(true);
    setPairingError(null);

    try {
      const result = await pairDevice({
        pairingCode: pairingCode.trim().toUpperCase(),
        seniorId: null, // will be assigned in step 3
        deviceName: deviceName.trim() || null
      });

      // Only proceed if we got a valid result with device_id
      if (result && result.id) {
        setPairedDevice(result);
        setStep(3);
        console.log('[DeviceWizard] Device paired successfully:', result.id);
      } else {
        // Unexpected: no error but also no valid device
        setPairingError('Gerät wurde gekoppelt, aber keine Device-ID erhalten. Bitte Support kontaktieren.');
        console.error('[DeviceWizard] Pairing returned invalid result:', result);
      }
    } catch (err) {
      // Enhanced error message display
      const errorMsg = err.message || err?.response?.data?.detail || 'Gerät konnte nicht gekoppelt werden.';
      setPairingError(errorMsg);
      console.error('[DeviceWizard] Pairing failed:', err);
    } finally {
      setIsPairing(false);
    }
  };

  const handleAssignSenior = () => {
    setStep(4);
  };

  const handleStartTestCall = async () => {
    if (!pairedDevice?.id) return;

    setStartingCall(true);
    try {
      await startTestCall(pairedDevice.id, user.id);
      alert('Testanruf gestartet! Bitte prüfe das Gerät.');
    } catch (error) {
      alert('Testanruf konnte nicht gestartet werden: ' + (error?.response?.data?.detail || error.message));
    } finally {
      setStartingCall(false);
    }
  };

  const handleFinish = () => {
    navigate('/family/dashboard');
  };

  const getSelectedSenior = () => {
    return seniors.find(s => s.id === selectedSeniorId);
  };

  if (!household) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#2C2640] via-[#1a1625] to-[#0f0a1a] flex items-center justify-center">
        <div className="text-white">Lade...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2C2640] via-[#1a1625] to-[#0f0a1a] py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-white mb-4">Gerät verbinden</h1>
          <p className="text-neutral-300 text-lg">Füge ein neues Gerät zu deinem Haushalt hinzu</p>
        </div>

        {/* Progress Stepper */}
        <div className="flex justify-between items-center mb-12 max-w-2xl mx-auto">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="flex items-center flex-1">
              <div className={`
                w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg
                transition-all duration-300 border-2
                ${s === step 
                  ? 'bg-gradient-to-br from-[#6DCEE8] to-[#5B4B8A] border-[#6DCEE8] text-white scale-110 shadow-lg shadow-[#6DCEE8]/50' 
                  : s < step 
                    ? 'bg-[#5B4B8A] border-[#5B4B8A] text-white' 
                    : 'bg-neutral-800/50 border-neutral-600 text-neutral-400'
                }
              `}>
                {s < step ? '✓' : s}
              </div>
              {s < 4 && (
                <div className={`flex-1 h-1 mx-2 rounded transition-all duration-300 ${
                  s < step ? 'bg-[#5B4B8A]' : 'bg-neutral-700'
                }`} />
              )}
            </div>
          ))}
        </div>

        {/* Step Content */}
        <div className="bg-neutral-900/80 backdrop-blur-xl rounded-2xl border border-neutral-700/50 p-8 shadow-2xl">
          {/* Step 1: Device Type Selection */}
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">Welches Gerät möchtest du verbinden?</h2>
                <p className="text-neutral-400">Wähle den Gerätetyp aus, den du hinzufügen möchtest</p>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                {DEVICE_TYPES.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => handleSelectDeviceType(type.id)}
                    className="group relative bg-neutral-800/50 backdrop-blur-sm rounded-xl p-6 border-2 border-neutral-700 hover:border-[#6DCEE8] transition-all duration-300 text-left hover:shadow-lg hover:shadow-[#6DCEE8]/20 hover:scale-105"
                  >
                    <div className="text-5xl mb-4">{type.icon}</div>
                    <h3 className="text-xl font-bold text-white mb-2 group-hover:text-[#6DCEE8] transition-colors">
                      {type.label}
                    </h3>
                    <p className="text-sm text-neutral-400">{type.description}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Pairing Code & Details */}
          {step === 2 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">Gerät koppeln</h2>
                <p className="text-neutral-400">Gib den Pairing-Code vom Gerät ein</p>
              </div>

              {/* Instructions */}
              <div className="bg-[#5B4B8A]/20 border border-[#5B4B8A]/40 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                  <span>ℹ️</span>
                  Anleitung
                </h3>
                {selectedDeviceType === 'tablet' && (
                  <p className="text-neutral-300">
                    Auf dem Hayat Care Tablet den Pairing-Bildschirm öffnen und den 6-stelligen Code ablesen.
                    Gib diesen Code unten ein.
                  </p>
                )}
                {selectedDeviceType === 'smartphone' && (
                  <p className="text-neutral-300">
                    In der Hayat Family App auf &quot;Mit Familie verbinden&quot; tippen und den angezeigten Code eingeben.
                  </p>
                )}
                {selectedDeviceType === 'other' && (
                  <p className="text-neutral-300">
                    Folge der Anleitung deines Geräts, um den Pairing-Code anzuzeigen.
                  </p>
                )}
              </div>

              {/* Error Message */}
              {pairingError && (
                <div className="bg-red-500/20 border border-red-500/40 rounded-xl p-4 text-red-200">
                  <span className="font-semibold">Fehler:</span> {pairingError}
                </div>
              )}

              {/* Form */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-300 mb-2">
                    Pairing-Code <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={pairingCode}
                    onChange={(e) => setPairingCode(e.target.value.toUpperCase())}
                    placeholder="z.B. ABC123"
                    maxLength={8}
                    className="w-full px-4 py-3 bg-neutral-800/50 border border-neutral-600 rounded-xl text-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-[#6DCEE8] focus:border-transparent transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-300 mb-2">
                    Gerätename <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={deviceName}
                    onChange={(e) => setDeviceName(e.target.value)}
                    placeholder="z.B. Tablet Wohnzimmer"
                    className="w-full px-4 py-3 bg-neutral-800/50 border border-neutral-600 rounded-xl text-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-[#6DCEE8] focus:border-transparent transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-300 mb-2">
                    Standort (optional)
                  </label>
                  <select
                    value={deviceLocation}
                    onChange={(e) => setDeviceLocation(e.target.value)}
                    className="w-full px-4 py-3 bg-neutral-800/50 border border-neutral-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#6DCEE8] focus:border-transparent transition-all"
                  >
                    <option value="">Bitte wählen...</option>
                    {LOCATIONS.map((loc) => (
                      <option key={loc.id} value={loc.id}>{loc.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-4 pt-4">
                <button
                  onClick={() => setStep(1)}
                  className="px-6 py-3 bg-neutral-700 text-white rounded-xl hover:bg-neutral-600 transition-colors"
                >
                  Zurück
                </button>
                <button
                  onClick={handlePairDevice}
                  disabled={isPairing || !pairingCode.trim() || !deviceName.trim()}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-[#6DCEE8] to-[#5B4B8A] text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-[#6DCEE8]/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isPairing ? 'Wird gekoppelt...' : 'Gerät koppeln'}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Assign Senior */}
          {step === 3 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">Senior zuweisen</h2>
                <p className="text-neutral-400">Weise das Gerät einem Senior zu oder überspringe diesen Schritt</p>
              </div>

              {loadingSeniors ? (
                <div className="text-center py-12">
                  <div className="animate-spin w-12 h-12 border-4 border-[#6DCEE8] border-t-transparent rounded-full mx-auto"></div>
                  <p className="text-neutral-400 mt-4">Lade Senioren...</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* No Senior Option */}
                  <button
                    onClick={() => setSelectedSeniorId(null)}
                    className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                      selectedSeniorId === null
                        ? 'border-[#6DCEE8] bg-[#6DCEE8]/10'
                        : 'border-neutral-700 bg-neutral-800/50 hover:border-neutral-600'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-neutral-700 flex items-center justify-center text-2xl">
                        ⏭️
                      </div>
                      <div>
                        <p className="font-semibold text-white">Noch keinem Senior zuweisen</p>
                        <p className="text-sm text-neutral-400">Du kannst das später in den Einstellungen ändern</p>
                      </div>
                    </div>
                  </button>

                  {/* Senior List */}
                  {seniors.map((senior) => (
                    <button
                      key={senior.id}
                      onClick={() => setSelectedSeniorId(senior.id)}
                      className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                        selectedSeniorId === senior.id
                          ? 'border-[#6DCEE8] bg-[#6DCEE8]/10'
                          : 'border-neutral-700 bg-neutral-800/50 hover:border-neutral-600'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#5B4B8A] to-[#6DCEE8] flex items-center justify-center text-white font-bold text-xl">
                          {senior.name?.charAt(0) || '?'}
                        </div>
                        <div>
                          <p className="font-semibold text-white">{senior.name}</p>
                          <p className="text-sm text-neutral-400">
                            {senior.language && `Sprache: ${senior.language}`}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}

                  {seniors.length === 0 && (
                    <div className="text-center py-8 text-neutral-400">
                      <p>Noch keine Senioren im Haushalt vorhanden.</p>
                      <p className="text-sm mt-2">Du kannst das Gerät später einem Senior zuweisen.</p>
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-4 pt-4">
                <button
                  onClick={() => setStep(2)}
                  className="px-6 py-3 bg-neutral-700 text-white rounded-xl hover:bg-neutral-600 transition-colors"
                >
                  Zurück
                </button>
                <button
                  onClick={handleAssignSenior}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-[#6DCEE8] to-[#5B4B8A] text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-[#6DCEE8]/50 transition-all"
                >
                  Weiter
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Summary & Finish */}
          {step === 4 && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-[#22C55E] to-[#16a34a] rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-4xl">✓</span>
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Gerät erfolgreich gekoppelt!</h2>
                <p className="text-neutral-400">Dein Gerät ist jetzt einsatzbereit</p>
              </div>

              {/* Summary Card */}
              <div className="bg-neutral-800/50 rounded-xl p-6 space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-neutral-700">
                  <span className="text-neutral-400">Gerätetyp</span>
                  <span className="text-white font-semibold">
                    {DEVICE_TYPES.find(t => t.id === selectedDeviceType)?.label}
                  </span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-neutral-700">
                  <span className="text-neutral-400">Gerätename</span>
                  <span className="text-white font-semibold">{deviceName}</span>
                </div>
                {deviceLocation && (
                  <div className="flex justify-between items-center py-3 border-b border-neutral-700">
                    <span className="text-neutral-400">Standort</span>
                    <span className="text-white font-semibold">
                      {LOCATIONS.find(l => l.id === deviceLocation)?.label}
                    </span>
                  </div>
                )}
                <div className="flex justify-between items-center py-3">
                  <span className="text-neutral-400">Zugewiesener Senior</span>
                  <span className="text-white font-semibold">
                    {selectedSeniorId ? getSelectedSenior()?.name : 'Noch nicht zugewiesen'}
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col gap-4 pt-4">
                {pairedDevice?.id && (
                  <button
                    onClick={handleStartTestCall}
                    disabled={startingCall}
                    className="w-full px-6 py-3 bg-[#F6B352] text-white rounded-xl font-semibold hover:bg-[#d49640] transition-all disabled:opacity-50"
                  >
                    {startingCall ? 'Startet...' : '📞 Testanruf starten'}
                  </button>
                )}
                <button
                  onClick={handleFinish}
                  className="w-full px-6 py-3 bg-gradient-to-r from-[#6DCEE8] to-[#5B4B8A] text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-[#6DCEE8]/50 transition-all"
                >
                  Fertig & zum Dashboard
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DeviceWizardAdminPage;
